import { faIcon } from "@utils/faIcon"

import { BULLET_CHARACTER } from "@particles/Bullet/Bullet"

import { Avatar } from "@atoms"

import {
    DefaultPriceBadge,
    InventoryLevelBadge,
    InventoryTrackingBadge,
    InventoryValueBadge,
    LabelsBadges,
    LastModifiedBadge,
    QuantityOnOrderBadge,
    TaxabilityBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import { PriceBookItemSubcardProps } from "@molecules/ObjectCard/ObjectCards/PriceBookItemCard/PriceBookItemCard.types"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import styles from "./ProductCard.module.scss"

const productIcon = faIcon({ name: "box-taped", style: "solid" })

function getManufacturerAndPartNumber(pricebookItem: PricebookItem): { manufacturer: string; partNumber: string } {
    return {
        manufacturer: pricebookItem.manufacturer || "No manufacturer",
        partNumber: pricebookItem.part_number || "No part number",
    }
}

export default function ProductCard(props: PriceBookItemSubcardProps) {
    const {
        pricebookItem,
        displayedAttributes,
        onClick,
        isLoading = false,
        isTabbable,
        className,
        ...errorProps
    } = props

    // The price and inventory chip are always shown when selected.
    const showPrice = shouldShowAttribute(displayedAttributes, "default_price")
    const showInventoryTracking = shouldShowAttribute(displayedAttributes, "track_inventory")
    const showTaxability = shouldShowAttribute(displayedAttributes, "default_is_taxable")
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(pricebookItem.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && pricebookItem.labels.length > 0

    const hasAttributes = showPrice || showInventoryTracking || showTaxability || showLastModified || showLabels

    // The inventory bar only renders for products that track inventory.
    const tracksInventory = pricebookItem.track_inventory
    const showInventoryLevel = shouldShowAttribute(displayedAttributes, "lowest_inventory_status")
    const showInventoryValue = shouldShowAttribute(displayedAttributes, "inventory_value")
    const showQuantityOnOrder = shouldShowAttribute(displayedAttributes, "total_quantity_on_order")
    const renderFooter =
        tracksInventory && (isLoading || showInventoryLevel || showInventoryValue || showQuantityOnOrder)

    // Inventory value is the total quantity (on hand plus on order) multiplied by the unit price, per the design.
    const inventoryValue = Number(pricebookItem.total_quantity) * Number(pricebookItem.default_price)

    const { manufacturer, partNumber } = getManufacturerAndPartNumber(pricebookItem)
    const hasManufacturerOrPartNumber = Boolean(pricebookItem.manufacturer || pricebookItem.part_number)

    return (
        <ObjectCard
            onClick={onClick}
            isLoading={isLoading}
            isTabbable={isTabbable}
            className={className}
            {...errorProps}
        >
            <ObjectCard.Header
                id="Product"
                subId={pricebookItem.subtype_label || undefined}
                subIdLabel="Subtype"
                statusBadge={
                    <ObjectStatusBadge objectType="pricebook_item" status={pricebookItem.status_label} size="md" />
                }
            />
            <ObjectCard.Body
                avatar={
                    <Avatar variant="object" icon={productIcon} src={pricebookItem.image_url || undefined} size="xl" />
                }
                primaryText={pricebookItem.description}
                secondaryText={
                    hasManufacturerOrPartNumber ? `${manufacturer} ${BULLET_CHARACTER} ${partNumber}` : undefined
                }
                secondaryTextPlaceholder={`No manufacturer ${BULLET_CHARACTER} No part number`}
                secondaryTextTooltip={`${manufacturer}\n${partNumber}`}
                secondaryTextTooltipAlways={true}
            />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showPrice && <DefaultPriceBadge value={pricebookItem.default_price} label="Default unit price" />}
                    {showInventoryTracking && <InventoryTrackingBadge tracksInventory={tracksInventory} />}
                    {showTaxability && <TaxabilityBadge isTaxable={pricebookItem.default_is_taxable} />}
                    {showLastModified && <LastModifiedBadge date={pricebookItem.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={pricebookItem.labels} />}
                </ObjectCard.Attributes>
            )}
            {renderFooter && (
                <ObjectCard.Footer className={styles.inventoryBar}>
                    {showInventoryLevel && (
                        <InventoryLevelBadge
                            status={pricebookItem.lowest_inventory_status}
                            quantity={pricebookItem.total_quantity}
                            quantityOnHand={pricebookItem.total_quantity_on_hand}
                            desired={pricebookItem.total_quantity_desired}
                        />
                    )}
                    {showInventoryValue && <InventoryValueBadge value={inventoryValue} />}
                    {showQuantityOnOrder && <QuantityOnOrderBadge count={pricebookItem.total_quantity_on_order} />}
                </ObjectCard.Footer>
            )}
        </ObjectCard>
    )
}
